import { useMemo, useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Store, Check, X, Ban, Plus, Pencil, Trash2, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { type AdminStore } from "@/types/admin.ui";
import {
  CompactStatCard,
  DataTable,
  FormInput,
  FormSelect,
  ImageUpload,
  PageHeader,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
  type DataTableFilterConfig,
} from "@/components/shared";
import { useToast } from "@/hooks/use-toast";
import {
  useAdminStores,
  useApproveStoreMutation,
  useCreateAdminStoreMutation,
  useDeleteAdminStoreMutation,
  useSuspendStoreMutation,
  useUpdateAdminStoreMutation,
} from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import { Skeleton } from "@/components/ui/skeleton";
import { UploadFolder } from "@/services/filesService";
import { StoreStatus } from "@/types/enums";
import { storesService } from "@/services/storesService";

const statusBadge: Record<string, string> = {
  approved: "bg-success/10 text-success border-success/20",
  pending: "bg-warning/10 text-warning border-warning/20",
  suspended: "bg-destructive/10 text-destructive border-destructive/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
};

const statusFilters: DataTableFilterConfig[] = [
  {
    id: "status",
    placeholder: "Status",
    options: [
      { label: "All Status", value: "all" },
      { label: "Approved", value: "approved" },
      { label: "Pending", value: "pending" },
      { label: "Suspended", value: "suspended" },
      { label: "Rejected", value: "rejected" },
    ],
  },
];

const emptyForm = {
  ownerName: "",
  ownerEmail: "",
  ownerPhone: "",
  storeName: "",
  description: "",
  phone: "",
  email: "",
  logoUrl: "",
  bannerUrl: "",
  fullAddress: "",
  city: "",
  state: "",
  pincode: "",
  latitude: "28.6139",
  longitude: "77.2090",
  deliveryRadius: "5",
  minOrderAmount: "0",
  deliveryCharge: "0",
  freeDeliveryAbove: "0",
  hasSelfDelivery: false,
  autoApprove: true,
};

const AdminStores = () => {
  const { toast } = useToast();
  const location = useLocation();
  const { data, isLoading } = useAdminStores();
  const approveStore = useApproveStoreMutation();
  const suspendStore = useSuspendStoreMutation();
  const createStore = useCreateAdminStoreMutation();
  const updateStore = useUpdateAdminStoreMutation();
  const deleteStore = useDeleteAdminStoreMutation();
  const stores = data?.items ?? [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminStore | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [credentialsInfo, setCredentialsInfo] = useState<string | null>(null);

  const table = useDataTable<AdminStore>({
    data: stores,
    searchKeys: ["name", "ownerName"],
    filterConfigs: statusFilters,
    filterPredicate: (row, state) =>
      state.filters.status === "all" || row.status === state.filters.status,
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = async (store: AdminStore) => {
    try {
      const detail = await storesService.getById(Number(store.id));
      setEditing(store);
      setForm({
        ownerName: "",
        ownerEmail: "",
        ownerPhone: "",
        storeName: detail.storeName,
        description: detail.description ?? "",
        phone: detail.phone,
        email: detail.email ?? "",
        logoUrl: detail.logoUrl ?? "",
        bannerUrl: detail.bannerUrl ?? "",
        fullAddress: detail.fullAddress,
        city: detail.city,
        state: detail.state,
        pincode: detail.pincode,
        latitude: String(detail.latitude),
        longitude: String(detail.longitude),
        deliveryRadius: String(detail.deliveryRadius),
        minOrderAmount: String(detail.minOrderAmount),
        deliveryCharge: String(detail.deliveryCharge),
        freeDeliveryAbove: String(detail.freeDeliveryAbove),
        hasSelfDelivery: detail.hasSelfDelivery,
        autoApprove: detail.status === StoreStatus.Active,
      });
      setDialogOpen(true);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  useEffect(() => {
    const editStoreId = (location.state as { editStoreId?: number } | null)?.editStoreId;
    if (!editStoreId || stores.length === 0) return;
    const store = stores.find((s) => Number(s.id) === editStoreId);
    if (store) {
      void openEdit(store);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, stores]);

  const handleSave = async () => {
    if (!form.storeName.trim() || !form.phone.trim()) return;

    const payload = {
      storeName: form.storeName,
      description: form.description || undefined,
      phone: form.phone,
      email: form.email || undefined,
      logoUrl: form.logoUrl || undefined,
      bannerUrl: form.bannerUrl || undefined,
      latitude: Number(form.latitude) || 0,
      longitude: Number(form.longitude) || 0,
      fullAddress: form.fullAddress,
      city: form.city,
      state: form.state,
      pincode: form.pincode,
      hasSelfDelivery: form.hasSelfDelivery,
      deliveryRadius: Number(form.deliveryRadius) || 0,
      minOrderAmount: Number(form.minOrderAmount) || 0,
      deliveryCharge: Number(form.deliveryCharge) || 0,
      freeDeliveryAbove: Number(form.freeDeliveryAbove) || 0,
    };

    try {
      if (editing) {
        await updateStore.mutateAsync({
          id: Number(editing.id),
          dto: {
            ...payload,
            status: form.autoApprove ? StoreStatus.Active : StoreStatus.Pending,
          },
        });
        toast({ title: "Store updated" });
      } else {
        if (!form.ownerName.trim() || !form.ownerEmail.trim() || !form.ownerPhone.trim()) {
          toast({ title: "Owner name, email, and phone are required", variant: "destructive" });
          return;
        }
        const result = await createStore.mutateAsync({
          ...payload,
          ownerName: form.ownerName,
          ownerEmail: form.ownerEmail,
          ownerPhone: form.ownerPhone,
          autoApprove: form.autoApprove,
        });
        // The API returns the generated credentials in the message
        setCredentialsInfo(result.message || "Store created");
      }
      setDialogOpen(false);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteStore.mutateAsync(Number(deleteId));
      toast({ title: "Store deleted" });
      setDeleteId(null);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await approveStore.mutateAsync({ id: Number(id), approve: true });
      toast({ title: "Store approved successfully" });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleSuspend = async (id: string) => {
    try {
      await suspendStore.mutateAsync({ id: Number(id), reason: "Suspended by admin" });
      toast({ title: "Store suspended", variant: "destructive" });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const stats = useMemo(
    () => ({
      pending: stores.filter((s) => s.status === "pending").length,
      approved: stores.filter((s) => s.status === "approved").length,
      suspended: stores.filter((s) => s.status === "suspended").length,
    }),
    [stores],
  );

  const columns: DataTableColumn<AdminStore>[] = [
    {
      id: "name",
      header: "Store",
      accessorKey: "name",
      sortable: true,
      mobilePriority: 1,
      cell: (store) => (
        <div>
          <Link to={`/admin/stores/${store.id}`} className="font-semibold hover:text-primary hover:underline">
            {store.name}
          </Link>
          <p className="text-xs text-muted-foreground">{store.address}</p>
        </div>
      ),
    },
    {
      id: "ownerName",
      header: "Owner",
      accessorKey: "ownerName",
      sortable: true,
      mobileLabel: "Owner",
      mobilePriority: 2,
      cell: (store) => (
        <div>
          <p className="text-sm">{store.ownerName}</p>
          <p className="text-xs text-muted-foreground">{store.phone}</p>
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      sortable: true,
      mobileLabel: "Status",
      mobilePriority: 3,
      cell: (store) => (
        <Badge variant="outline" className={statusBadge[store.status]}>
          {store.status}
        </Badge>
      ),
    },
    {
      id: "totalOrders",
      header: "Orders",
      accessorKey: "totalOrders",
      sortable: true,
      align: "right",
      mobileLabel: "Orders",
      mobilePriority: 4,
    },
    {
      id: "joinedDate",
      header: "Joined",
      accessorKey: "joinedDate",
      sortable: true,
      hideOnMobile: true,
      className: "text-sm text-muted-foreground",
    },
    {
      id: "actions",
      header: "Actions",
      align: "center",
      hideOnMobile: true,
      cell: (store) => (
        <div className="flex items-center justify-center gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" asChild>
            <Link to={`/admin/stores/${store.id}`}>
              <Eye className="h-4 w-4" />
            </Link>
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(store)}>
            <Pencil className="h-4 w-4" />
          </Button>
          {store.status === "pending" && (
            <Button size="icon" variant="ghost" className="h-8 w-8 text-success" onClick={() => handleApprove(store.id)}>
              <Check className="h-4 w-4" />
            </Button>
          )}
          {store.status === "approved" && (
            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleSuspend(store.id)}>
              <Ban className="h-4 w-4" />
            </Button>
          )}
          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setDeleteId(store.id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Store Management"
        description="Create, approve, and manage vendor stores."
        actions={
          <Button className="gap-2" onClick={openCreate}>
            <Plus className="h-4 w-4" /> Add Store
          </Button>
        }
      />

      <StatsGrid columns={3}>
        <CompactStatCard title="Approved" value={stats.approved} icon={Check} />
        <CompactStatCard title="Pending" value={stats.pending} icon={Store} />
        <CompactStatCard title="Suspended" value={stats.suspended} icon={Ban} />
      </StatsGrid>

      {isLoading ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : (
        <DataTable
          title="All Stores"
          columns={columns}
          data={table.rows}
          getRowId={(row) => row.id}
          search={table.state.search}
          onSearchChange={table.setSearch}
          searchPlaceholder="Search stores..."
          filters={statusFilters}
          filterValues={table.state.filters}
          onFilterChange={table.setFilter}
          sort={table.state.sort}
          onSortChange={table.toggleSort}
          pagination={table.pagination}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
          renderMobileCard={(store) => (
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold">{store.name}</p>
                  <p className="text-xs text-muted-foreground">{store.ownerName}</p>
                </div>
                <Badge variant="outline" className={statusBadge[store.status]}>{store.status}</Badge>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" asChild>
                  <Link to={`/admin/stores/${store.id}`}>View</Link>
                </Button>
                <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(store)}>Edit</Button>
                {store.status === "pending" && (
                  <Button size="sm" className="flex-1" onClick={() => handleApprove(store.id)}>Approve</Button>
                )}
              </div>
            </div>
          )}
        />
      )}

      <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-xl">
          <SheetHeader className="border-b px-6 py-5 text-left">
            <SheetTitle>{editing ? "Edit Store" : "Create Store"}</SheetTitle>
            <SheetDescription>
              {editing ? "Update store details and branding." : "Register a new vendor store on the platform."}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {!editing && (
              <div className="space-y-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
                <p className="text-sm font-medium text-primary">Store Owner (auto-created)</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormInput label="Owner name *" value={form.ownerName} onChange={(e) => setForm({ ...form, ownerName: e.target.value })} placeholder="e.g. Rahul Sharma" />
                  <FormInput label="Owner phone *" value={form.ownerPhone} onChange={(e) => setForm({ ...form, ownerPhone: e.target.value })} placeholder="e.g. 9876543210" />
                </div>
                <FormInput label="Owner email *" value={form.ownerEmail} onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })} placeholder="e.g. owner@store.com" />
                <p className="text-xs text-muted-foreground">A login account will be auto-created with generated credentials.</p>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormInput label="Store name *" value={form.storeName} onChange={(e) => setForm({ ...form, storeName: e.target.value })} />
              <FormInput label="Phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <FormInput label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <FormInput label="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <FormInput label="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
              <FormInput label="Pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
            </div>
            <FormInput label="Address" value={form.fullAddress} onChange={(e) => setForm({ ...form, fullAddress: e.target.value })} />
            <FormInput label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

            <div className="space-y-4 rounded-lg border border-border p-4">
              <p className="text-sm font-medium">Store branding</p>
              <ImageUpload
                label="Logo"
                variant="logo"
                layout="stacked"
                value={form.logoUrl}
                onChange={(url) => setForm({ ...form, logoUrl: url })}
                folder={UploadFolder.Stores}
              />
              <ImageUpload
                label="Banner"
                variant="banner"
                layout="stacked"
                value={form.bannerUrl}
                onChange={(url) => setForm({ ...form, bannerUrl: url })}
                folder={UploadFolder.Stores}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Auto-approve / active</Label>
              <Switch checked={form.autoApprove} onCheckedChange={(v) => setForm({ ...form, autoApprove: v })} />
            </div>
          </div>

          <SheetFooter className="mt-auto border-t bg-background px-6 py-4 sm:flex-row sm:justify-end sm:gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createStore.isPending || updateStore.isPending}>Save</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={Boolean(deleteId)} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete store?</AlertDialogTitle>
            <AlertDialogDescription>This soft-deletes the store from the platform.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Credentials dialog shown after store creation */}
      <AlertDialog open={Boolean(credentialsInfo)} onOpenChange={(open) => !open && setCredentialsInfo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-success">✅ Store Created Successfully</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="text-sm">The store owner account has been created with the following credentials. Please share these with the store owner:</p>
                <div className="rounded-lg border bg-muted/50 p-4 font-mono text-sm whitespace-pre-wrap break-all">
                  {credentialsInfo}
                </div>
                <p className="text-xs text-muted-foreground">⚠️ Save these credentials now. The password cannot be retrieved again.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => {
              if (credentialsInfo) navigator.clipboard.writeText(credentialsInfo);
              setCredentialsInfo(null);
            }}>
              Copy & Close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminStores;
